/**
 * Connect your own Claude — a key that acts as you over MCP (ported from Device Lab, 2026-09-23).
 *
 * The key works only through the MCP server, which names you to the API: the phone your agent takes
 * is your claim (this workspace shows it as yours and you can watch it), and the one-person-per-phone
 * rules apply to it as to this page. No choices on this screen — the key's reach is fixed on the
 * service — and the connection instructions sit beside the key, because "where do I put this?" is the
 * question people actually ask.
 *
 * **Connect first, a key last** (ported from Device Lab, 2026-10-08): in claude.ai, Claude Desktop or
 * Claude Code the person adds the connector with **no key** and presses Connect; the service's OAuth
 * sends them to our consent page (`features/oauth/Consent.tsx`), and the key their Claude gets appears
 * in the list below like any other, revoked the same way. The manual key stays for scripts.
 */

import { useState } from 'react'

import type { MyKey } from '../../api/types'
import { useT } from '../../i18n'
import { useApp } from '../../lib/context'
import { usePoll } from '../../lib/poll'
import { Badge, Card, Empty, Pill, Skeletons, Status } from '../../ui/components'
import { ConfirmDanger } from '../../ui/ConfirmDanger'

//: The service's address — the MCP server sits behind the same host at /mcp.
const MCP_URL = 'https://tik-video.mornhouse.com/mcp'

export function MyKeysPanel() {
  const { api, authEpoch } = useApp()
  const t = useT()
  const keys = usePoll(() => api.myKeys(), 30000, [api, authEpoch])
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)
  //: The one time the secret exists outside the service. State only, never storage.
  const [issued, setIssued] = useState<string | null>(null)
  const [pending, setPending] = useState<{ row: MyKey; act: 'revoke' | 'delete' } | null>(null)

  const run = async (fn: () => Promise<unknown>) => {
    setProblem(null)
    setBusy(true)
    try {
      await fn()
      keys.refresh()
    } catch (e) {
      const detail = (e as { detail?: { detail?: { hint?: string } } })?.detail?.detail
      setProblem(detail?.hint ?? (e instanceof Error ? e.message : String(e)))
    } finally {
      setBusy(false)
      setPending(null)
    }
  }

  const command = `claude mcp add --transport http ferma-hub ${MCP_URL} --header "Authorization: Bearer ${issued ?? '<your key>'}"`
  const plainCommand = `claude mcp add --transport http ferma-hub ${MCP_URL}`
  const copy = (text: string) => void navigator.clipboard?.writeText(text)

  return (
    <>
      <Card title={t.myClaude.title} sub={t.myClaude.sub}>
        <h3 className="howto-title">{t.myClaude.connectTitle}</h3>
        <ol className="howto">{t.myClaude.connectSteps.map((x) => <li key={x}>{x}</li>)}</ol>
        <div className="row">
          <code className="grow mono">{MCP_URL}</code>
          <button className="ghost tiny" onClick={() => copy(MCP_URL)}>{t.myClaude.copy}</button>
        </div>
        <div className="small">{t.myClaude.connectNote}</div>
        <div className="row">
          <code className="grow mono" style={{ overflowWrap: 'anywhere' }}>{plainCommand}</code>
          <button className="ghost tiny" onClick={() => copy(plainCommand)}>{t.myClaude.copy}</button>
        </div>
      </Card>

      <Card title={t.myClaude.manualTitle} sub={t.myClaude.manualSub}>
        <div className="row">
          <button disabled={busy}
                  onClick={() => void run(async () => setIssued((await api.createMyKey()).token))}>
            {t.myClaude.create}
          </button>
          <span className="tiny dim">{t.myClaude.shownOnce}</span>
        </div>
        <Status text={problem} bad />
        {issued && (
          <>
            <div className="small">{t.myClaude.issued}</div>
            <div className="row">
              <code className="grow mono" style={{ overflowWrap: 'anywhere' }}>{issued}</code>
              <button onClick={() => copy(issued)}>{t.myClaude.copy}</button>
            </div>
          </>
        )}
        <div className="small">{t.myClaude.web}</div>
        <div className="small">{t.myClaude.code}</div>
        <div className="row">
          <code className="grow mono" style={{ overflowWrap: 'anywhere' }}>{command}</code>
          <button className="ghost tiny" onClick={() => copy(command)}>{t.myClaude.copy}</button>
        </div>
      </Card>

      <Card title={t.myClaude.mine} sub={t.myClaude.mineSub}>
        {!keys.data && !keys.error && <Skeletons count={1} />}
        <Status text={keys.error ? keys.error.message : null} bad />
        {keys.data?.keys.length === 0 && <Empty>{t.myClaude.none}</Empty>}
        {(keys.data?.keys ?? []).map((row) => (
          <div className="row" key={row.key_id}>
            <b>{row.name}</b>
            <Pill mono>{row.prefix}…</Pill>
            {row.live ? <Badge kind="ok">{t.myClaude.live}</Badge> : <Badge kind="warn">{t.myClaude.revoked}</Badge>}
            <span className="tiny dim grow">{row.created_at.slice(0, 10)}</span>
            {row.live && (
              <button className="ghost tiny" disabled={busy}
                      onClick={() => setPending({ row, act: 'revoke' })}>{t.myClaude.revoke}</button>
            )}
            <button className="ghost tiny" disabled={busy}
                    onClick={() => setPending({ row, act: 'delete' })}>{t.myClaude.remove}</button>
          </div>
        ))}
      </Card>

      {pending && (
        <ConfirmDanger
          title={pending.row.name}
          confirmLabel={pending.act === 'revoke' ? t.myClaude.revoke : t.myClaude.remove}
          onCancel={() => setPending(null)}
          onConfirm={() => void run(() => pending.act === 'revoke'
            ? api.revokeMyKey(pending.row.key_id) : api.deleteMyKey(pending.row.key_id))}
        >
          <div>{pending.act === 'revoke' ? t.myClaude.confirmRevoke : t.myClaude.confirmRemove}</div>
        </ConfirmDanger>
      )}
    </>
  )
}
